/* oxlint-disable eslint/max-lines */
/**
 * ClinicalWebPanel — drill-in view for Clinical web in source control.
 *
 * Country / jurisdiction write Evidence settings. Specialty updates the
 * account specialty used for Evidence personalisation. Search behaviour stays
 * the existing profile-based personalisation path (no request-scoped overrides).
 */

import { JurisdictionSelect } from '@/components/ui/jurisdiction-select/JurisdictionSelect';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@heidi/design-system/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@heidi/design-system/popover';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { Switch } from '@heidi/design-system/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import CountrySelect from '@/components/ui/select/country-select';
import { useMutateUpdateSettings } from '@/hooks/mutation/useMutateUpdateSettings';
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useQueryUser } from '@heidi/workspace/data';
import { useEffectiveEvidenceLocation } from '@/hooks/useEffectiveEvidenceLocation';
import { sortedCountries, type CountryType } from '@heidi/utils/countries';
import { cn } from '@/lib/cn';
import type { TranslationKey } from '@heidi/runtime/i18n';
import { useTranslations } from '@heidi/runtime/i18n';
import { Check, ChevronDown, ChevronLeft } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSourceControlActions } from '../hooks/useSourceControlActions';
import { personalisedSourceTooltipMessages } from '../personalised-source-avatars.utils';

interface ClinicalWebPanelProps {
  onBack: () => void;
  hasEvidencePro: boolean;
  isAuthenticated: boolean;
  /** Team enforcement: admin disabled clinical sources — hide the guidelines toggle entirely. */
  guidelinesTeamDisabled: boolean;
  /** Team enforcement: clinical sources not changeable — show the toggle but lock it. */
  guidelinesTeamLocked: boolean;
  teamName?: string;
}

export const ClinicalWebPanel = ({
  onBack,
  hasEvidencePro,
  isAuthenticated,
  guidelinesTeamDisabled,
  guidelinesTeamLocked,
  teamName,
}: ClinicalWebPanelProps) => {
  const { t } = useTranslations();
  const intl = useIntl();
  const guidelinesOnlyLabel = intl.formatMessage(
    personalisedSourceTooltipMessages.guidelinesOnly
  );
  const { baseProps, trackJurisdictionChanged, trackSpecialtyChanged } =
    useEvidenceAnalytics();
  const { toggleState, handleClinicalSourcesToggle } = useSourceControlActions(baseProps);
  const { mutate: updateUser } = useMutateUpdateUser();
  const { mutate: updateSettings } = useMutateUpdateSettings({ showToast: false });

  const {
    country: evidenceCountry,
    jurisdiction: evidenceJurisdiction,
    teamCountry,
    teamJurisdiction,
    teamName: locationTeamName,
  } = useEffectiveEvidenceLocation();

  const { data: userSpecialtyId } = useQueryUser((data) => data?.specialty_id);
  const { data: specialtiesData } = useQuerySpecialties();

  const [localCountry, setLocalCountry] = useState<string | null>(null);
  const [localJurisdiction, setLocalJurisdiction] = useState<string | null>(null);
  const [specialtyPopoverOpen, setSpecialtyPopoverOpen] = useState(false);
  const [specialtySearch, setSpecialtySearch] = useState('');

  const selectedCountryName = localCountry ?? evidenceCountry;
  const selectedCountry = sortedCountries.find((c) => c.name === selectedCountryName);
  const currentCountry = sortedCountries.find((c) => c.name === evidenceCountry);
  const selectedJurisdiction = localJurisdiction ?? evidenceJurisdiction;

  const isGuidelinesOnlyOn =
    toggleState.localGuidelines && toggleState.primaryEvidence;

  const specialties = useMemo(() => specialtiesData?.specialties ?? [], [specialtiesData]);
  const selectedSpecialty = specialties.find((s) => s.id === userSpecialtyId);
  const specialtyDisplayName =
    selectedSpecialty?.localizedName ?? t('settings.evidence.notSet' as TranslationKey);
  const filteredSpecialties = useMemo(
    () =>
      specialties.filter((specialty) =>
        specialty.localizedName.toLowerCase().includes(specialtySearch.toLowerCase())
      ),
    [specialties, specialtySearch]
  );

  const handleSpecialtyChange = useCallback(
    (specialtyId: string) => {
      if (!isAuthenticated) return;
      trackSpecialtyChanged(specialtyId);
      updateSettings({
        specialty_id: specialtyId,
        other_specialty: null,
      });
      setSpecialtyPopoverOpen(false);
      setSpecialtySearch('');
    },
    [isAuthenticated, trackSpecialtyChanged, updateSettings]
  );

  const handleCountryChange = useCallback(
    (country: CountryType) => {
      if (!isAuthenticated) return;
      setLocalCountry(country.name);
      setLocalJurisdiction(null);
      updateUser({
        evidence_settings: {
          country: country.name,
          jurisdiction: null,
        },
      });
    },
    [isAuthenticated, updateUser]
  );

  const handleJurisdictionChange = useCallback(
    (jurisdiction: string) => {
      if (!isAuthenticated) return;
      setLocalJurisdiction(jurisdiction);
      updateUser({
        evidence_settings: {
          jurisdiction,
        },
      });
      trackJurisdictionChanged(jurisdiction);
    },
    [isAuthenticated, updateUser, trackJurisdictionChanged]
  );

  const guidelinesDisabled = !hasEvidencePro || !isAuthenticated || guidelinesTeamLocked;
  const guidelinesTooltip = guidelinesTeamLocked
    ? `Required by ${teamName ?? 'your team'}`
    : undefined;

  const guidelinesSwitch = (
    <Switch
      checked={isGuidelinesOnlyOn}
      onCheckedChange={(value) => {
        if (!isAuthenticated) return;
        handleClinicalSourcesToggle(value);
      }}
      disabled={guidelinesDisabled}
      size="xs"
      aria-label={guidelinesOnlyLabel}
    />
  );

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 rounded-md p-2 text-left transition-colors hover:bg-surface-hover"
        aria-label={intl.formatMessage({
          defaultMessage: 'Back to sources',
          id: 'MVluQkDdTC',
        })}
      >
        <ChevronLeft size={16} className="text-text-secondary rtl:-scale-x-100" />
        <TypographyP2 className="font-normal text-text-primary">
          {t('sources.sourceControl.webSearch' as TranslationKey)}
        </TypographyP2>
      </button>

      {!guidelinesTeamDisabled && (
        <div className="rounded-lg border border-border bg-background-tertiary p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-col gap-1">
              <TypographyP2Bold className="text-text-primary">
                {guidelinesOnlyLabel}
              </TypographyP2Bold>
              <TypographyCaption className="text-text-tertiary">
                {intl.formatMessage({
                  defaultMessage:
                    'Narrows web results to formal recommendations. Partners and your library are unaffected.',
                  id: 'mQ6UBvhoRa',
                })}
              </TypographyCaption>
              <TypographyCaption className="text-text-tertiary">
                {intl.formatMessage({
                  defaultMessage: 'Searches may take longer when this is on.',
                  id: 'U3meJmBV1o',
                })}
              </TypographyCaption>
            </div>
            {guidelinesTooltip ? (
              <Tooltip>
                <TooltipTrigger asChild>{guidelinesSwitch}</TooltipTrigger>
                <TooltipContent side="top">{guidelinesTooltip}</TooltipContent>
              </Tooltip>
            ) : (
              guidelinesSwitch
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3 px-1">
        <div className="flex flex-col gap-1">
          <TypographyP2Bold className="text-text-primary">
            {intl.formatMessage({
              defaultMessage: 'Region',
              id: 'lnaWo/vCHM',
            })}
          </TypographyP2Bold>
          {teamCountry.enforced ? (
            <div className="rounded-md border border-border bg-background-tertiary px-3 py-2">
              <TypographyP2>
                {evidenceCountry ?? t('settings.evidence.notSet')}
              </TypographyP2>
            </div>
          ) : (
            <CountrySelect
              value={selectedCountry ?? currentCountry}
              setValue={handleCountryChange}
            />
          )}
        </div>

        <div className="flex flex-col gap-1">
          <TypographyP2Bold className="text-text-primary">
            {t('settings.evidence.evidenceStateRegion')}
          </TypographyP2Bold>
          {teamJurisdiction.enforced ? (
            <div className="rounded-md border border-border bg-background-tertiary px-3 py-2">
              <TypographyP2>
                {evidenceJurisdiction ?? t('settings.evidence.notSet')}
              </TypographyP2>
            </div>
          ) : (
            <JurisdictionSelect
              countryName={selectedCountryName}
              value={selectedJurisdiction ?? null}
              onChange={handleJurisdictionChange}
            />
          )}
        </div>

        <div className="flex flex-col gap-1">
          <TypographyP2Bold className="text-text-primary">
            {t('settings.evidence.specialty' as TranslationKey)}
          </TypographyP2Bold>
          <Popover open={specialtyPopoverOpen} onOpenChange={setSpecialtyPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-background-tertiary px-3 py-2 text-left"
                aria-label={t('settings.evidence.specialty' as TranslationKey)}
                aria-expanded={specialtyPopoverOpen}
              >
                <TypographyP2 className="truncate font-medium">
                  {specialtyDisplayName}
                </TypographyP2>
                <ChevronDown size={14} className="shrink-0 text-text-tertiary" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="start">
              <Command className="rounded-md">
                <CommandInput
                  placeholder={t('account.searchClinicianTypes')}
                  value={specialtySearch}
                  onValueChange={setSpecialtySearch}
                />
                <CommandEmpty>{t('common.noResultsFound')}</CommandEmpty>
                <ScrollArea
                  className="h-48"
                  viewportClassName="[&>div]:!min-w-0 [&>div]:!block"
                >
                  <CommandGroup>
                    {filteredSpecialties.map((specialty) => (
                      <CommandItem
                        key={specialty.id}
                        value={specialty.localizedName}
                        onSelect={() => handleSpecialtyChange(specialty.id)}
                        className="min-w-0"
                      >
                        <Check
                          className={cn(
                            'mr-2 size-4 shrink-0',
                            userSpecialtyId === specialty.id ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <TypographyP2 className="truncate">
                          {specialty.localizedName}
                        </TypographyP2>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </ScrollArea>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {(teamCountry.enforced || teamJurisdiction.enforced) && locationTeamName && (
        <TypographyCaption className="px-1 text-text-tertiary">
          {t('sources.sourceControl.managedByTeamAdmin' as TranslationKey, {
            teamName: locationTeamName,
          })}
        </TypographyCaption>
      )}
    </div>
  );
};
